import React from "react";
import { consolidationRequest as api } from "../../api/consolidationApi.js";
import useAccess from "../../hooks/useAccess.jsx";
import { ConsolidationDraftProvider } from "../../hooks/useConsolidationDrafts.jsx";
import { ConsolidationAspirationFee } from "../consolidation/OrderRateExceptions.jsx";
import SheetEditor from "../consolidation/SheetEditor.jsx";
import { BillToCompaniesContext } from "../BillToSelect.jsx";

// Relevé de Consolidation du camion, modifiable sur place comme dans la Consolidation (mêmes brouillons).
export default function GeotabReleve({ date, period, truckId }) {
  const { access, hasPermission } = useAccess();
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const load = React.useCallback(() => api(`?${new URLSearchParams({ from_date: date, to_date: date })}`).then((result) => {
    setData(result);
    setError(null);
    return result;
  }), [date]);
  React.useEffect(() => {
    setData(null);
    load().catch(setError);
  }, [load]);
  // Employés de « Qui a conduit ? », comme dans la Consolidation (même lecture, même droit).
  const [employees, setEmployees] = React.useState([]);
  React.useEffect(() => {
    const controller = new AbortController();
    api("/options", { signal: controller.signal }).then((result) => setEmployees(result.employees || []), () => {});
    return () => controller.abort();
  }, []);

  const rights = {
    write: hasPermission("billing.consolidation.write"),
    validate: hasPermission("billing.consolidation.validate"), reopen: hasPermission("billing.consolidation.reopen"),
    dispatchReopen: hasPermission("dispatch.day.reopen"), geotab: hasPermission("billing.consolidation.geotab.read") || hasPermission("geotab.read"),
    editOrders: hasPermission("data_manager.orders.write"),
  };
  const run = async (work) => {
    setBusy(true);
    setError(null);
    try { await work(); } catch (e) { setError(e); } finally { setBusy(false); }
  };
  const onChanged = ({ propagateError = false } = {}) => load().catch((e) => {
    setError(e);
    if (propagateError) throw e;
  });

  const day = data?.days?.find((candidate) => candidate.date === date);
  const routes = day ? [...day.driver_groups.flatMap((group) => group.routes), ...(day.unassigned_routes || [])] : [];
  const route = routes.find((candidate) => candidate.sheet.truck_label === truckId && (candidate.sheet.dispatch_period || "day") === period);
  const scope = `${access?.userId || ""}:${data?.company_id || ""}`;
  return (
    <div className="grid min-w-0 gap-1">
      {error && <p role="alert" className="rounded-[4px] border border-red-200 bg-red-50 px-2 py-1 text-[12px] text-red-800">{error.message}</p>}
      {busy && <p role="status" className="text-[12px] text-slate-500">Enregistrement ou vérification en cours…</p>}
      {!data && !error ? <p role="status" className="px-1 text-[12px] text-slate-500">Chargement du relevé…</p>
        : data && !route ? <p className="px-1 text-[12px] text-slate-600">Aucun relevé de Consolidation pour ce camion ce jour-là.</p>
        : route && (
          <ConsolidationDraftProvider key={scope} scope={scope}>
            <ConsolidationAspirationFee.Provider value={data.aspiration_fee || null}>
              <BillToCompaniesContext.Provider value={data.bill_to_companies || []}>
                <SheetEditor initial={route.sheet} embedded geotabGantt={false} rights={rights} run={run} busy={busy} onChanged={onChanged} employees={employees} />
              </BillToCompaniesContext.Provider>
            </ConsolidationAspirationFee.Provider>
          </ConsolidationDraftProvider>
        )}
    </div>
  );
}
